import { PenLineIcon } from 'lucide-react';
import { useState } from 'react';

import {
  getGetAssetsContractsAssetContractQueryKey,
  getGetSignedAssetsContractsAssetContractQueryKey,
  useGetAssetContracts,
  useRequestContractSignatureAssetContract,
} from '@ping/api';
import { t } from '@ping/helpers';
import { queryClient } from '@ping/providers/ReactQueryProvider';
import { Button, Modal, Toast } from '@ping/uikit';

import { ContractItem } from '../ContractList/ContractItem';
import { ContractSigningQrCode } from './ContractSigningQrCode';
import style from './style.module.scss';

interface IAssetContractSignModalProps {
  isOpen: boolean;
  onClose: () => void;
  assetId: string;
  assetName: string;
  onSigned?: () => void;
}

export const AssetContractSignModal = ({
  isOpen,
  onClose,
  assetName,
  onSigned,
  assetId,
}: IAssetContractSignModalProps) => {
  const [signingLink, setSigningLink] = useState<string | null>(null);
  const contract: any = useGetAssetContracts(assetId, {
    query: {
      enabled: isOpen && !!assetId,
    },
  });

  const requestContractSignature = useRequestContractSignatureAssetContract({
    mutation: {
      onSuccess: data => {
        if (data && typeof data === 'string' && data.length > 0) {
          setSigningLink(data);
        } else {
          handleSigningComplete();
        }
      },
      onError: () => {
        Toast.error({ title: t('Failed to request contract signing. Please try again.') });
      },
    },
  });

  const handleSignContract = () => {
    requestContractSignature.mutate({ data: { assetId } });
  };

  const handleSigningComplete = () => {
    queryClient.invalidateQueries(getGetAssetsContractsAssetContractQueryKey());
    queryClient.invalidateQueries(getGetSignedAssetsContractsAssetContractQueryKey());
    handleClose();
    Toast.success({ title: t('Contract signed successfully') });
    onSigned?.();
  };

  const handleClose = () => {
    onClose();
    setSigningLink(null);
  };

  return (
    <Modal.Root isOpen={isOpen} onOverlayPress={handleClose} onCloseButtonPress={handleClose}>
      <Modal.Portal className={style['asset-contract-sign-modal']} isDismissable>
        <Modal.CloseButton className={style['asset-contract-sign-modal__close']} />

        <Modal.Header className={style['asset-contract-sign-modal__header']}>
          <Modal.Title className={style['asset-contract-sign-modal__title']}>
            {signingLink ? t('Sign Contract') : t('Asset Contract')}
          </Modal.Title>

          <Modal.Subtitle className={style['asset-contract-sign-modal__subtitle']}>
            {signingLink
              ? t('Scan QR code with your CorePass app to sign the contract.')
              : t('You need to sign a contract before trading %s.', assetName)}
          </Modal.Subtitle>
        </Modal.Header>

        {signingLink && !requestContractSignature.isLoading ? (
          <Modal.Content className={style['asset-contract-sign-modal__content']}>
            <ContractSigningQrCode signingLink={signingLink} assetId={assetId} onSigned={handleSigningComplete} />
            <Button
              className={style['asset-contract-sign-modal__cancel']}
              onClick={handleClose}
              size='large'
              uppercase
              type='secondary'
            >
              {t('Cancel')}
            </Button>
          </Modal.Content>
        ) : (
          <>
            <Modal.Content className={style['asset-contract-sign-modal__content']}>
              <ContractItem contract={contract.data} isLoading={contract.isLoading} isReadonly />
            </Modal.Content>
            <Modal.Actions className={style['asset-contract-sign-modal__actions']}>
              <Button
                className={style['asset-contract-sign-modal__sign-button']}
                disabled={requestContractSignature.isLoading || contract.isLoading}
                isLoading={requestContractSignature.isLoading}
                onClick={handleSignContract}
                size='large'
                uppercase
              >
                <PenLineIcon size={14} />
                {t('Sign Contract')}
              </Button>
            </Modal.Actions>
          </>
        )}
      </Modal.Portal>
    </Modal.Root>
  );
};
